import { DOCUMENT } from '@angular/common';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import {
    NEVER,
    Observable,
    exhaustMap,
    filter,
    firstValueFrom,
    map,
    merge,
    of,
    switchMap,
    tap,
    withLatestFrom
} from 'rxjs';

import {
    ImpersonationService,
    ON_BEHALF_OF_QUERY_PARAM,
    RESET_ON_BEHALF_OF_QUERY_PARAM
} from '@mobi/rwc-utils-azure-ng-jslib';
import { IMPERSONATE_ACTION, ImpersonationUrlListenerService } from '@mobi/rwc-utils-azure-ng-jslib';
import { Logger } from '@mobi/rwc-utils-common-ng-jslib';

import { AuthenticationService, CipUserId } from '../../authentication.service';
import { CipAuthHelperService } from '../cip-auth-helper.service';
import { CIP_CONFIG_ID } from '../cip-auth.config';

import { CipImpersonationIntegrationService } from './cip-impersonation-integration.service';
import { ImpersonableUsersService } from './impersonable-users.service';

export class CipImpersonationService extends ImpersonationService {
    #impersonationIntegrationService = inject(CipImpersonationIntegrationService);
    #authenticationService = inject(AuthenticationService);
    #oidcSecurityService = inject(OidcSecurityService);
    #cipAuthHelperService = inject(CipAuthHelperService);
    #router = inject(Router);
    #logger = inject(Logger).createCustomLogger('Impersonation');
    #window: Window = inject(DOCUMENT).defaultView as Window;
    #url$ = inject(ImpersonationUrlListenerService).url$;

    /**
     * unused, just to fulfill the interface (always undefined)
     */
    readonly onBehalfOf = undefined;

    readonly impersonableUsers$ = inject(ImpersonableUsersService).impersonableUsers$;
    readonly allowedTestUsers$ = this.impersonableUsers$.pipe(
        map(users => users.map(u => ({ userId: u.userId, anzeigeName: u.label, name: '', vorName: '' })))
    );

    /**
     * if the given name is falsy, impersonation will be reset
     */
    async switchUser(userId?: string | null): Promise<true> {
        return firstValueFrom(this.#impersonate(userId));
    }

    #impersonate(userId?: string | null): Observable<true> {
        return this.#authenticationService.getUserData().pipe(
            filter(loggedInUser => loggedInUser.preferred_username !== userId?.toLowerCase()),
            // we want to avoid fetching the user lists in those two cases
            exhaustMap(loggedInUser => {
                // either want to logoff or we're already impersonating
                if (!userId || !!loggedInUser.impersonator?.username) {
                    this.#logger.info(
                        userId ? 'Already impersonated, resetting impersonation' : 'Resetting impersonation'
                    );
                    const url = this.#urlWithoutOnBehalfOf(loggedInUser.preferred_username);
                    this.#cipAuthHelperService.savePostLoginUrl(url);
                    return this.#oidcSecurityService.logoff(CIP_CONFIG_ID).pipe(switchMap(() => NEVER));
                }
                return of(loggedInUser);
            }),
            switchMap(loggedInUser =>
                this.impersonableUsers$.pipe(
                    map(users => users.find(u => u.userId.toLowerCase() === userId?.toLowerCase())),
                    exhaustMap(user => {
                        // at that point we know that userId is set and that we are imperonating
                        this.#logger.info(`Will perform impersonation for user ${user?.userId}`);
                        if (user) {
                            return this.#impersonationIntegrationService.impersonate(user).pipe(
                                tap(() => {
                                    this.#oidcSecurityService.logoffLocal(CIP_CONFIG_ID);
                                    this.#window.location.reload();
                                })
                            );
                        } else {
                            const msg = `User ${loggedInUser.preferred_username} does no have '${userId}' in impersonation allowed list`;
                            this.#logger.error(
                                `${msg}. Please add ${userId} to the allowed list to be able to impersonate this user.`
                            );
                            return NEVER;
                        }
                    })
                )
            ),
            map(() => true)
        );
    }

    constructor() {
        super();
        this.#initUrlListeners();
    }

    #initUrlListeners(): void {
        merge(
            this.#url$.pipe(
                filter(
                    ([action, _]) => action === RESET_ON_BEHALF_OF_QUERY_PARAM || action === ON_BEHALF_OF_QUERY_PARAM
                ),
                switchMap(([parameter, url]) => {
                    url.searchParams.delete(parameter);
                    const updatedUrl = url.href.replace(url.origin, '');
                    return this.#router.navigateByUrl(updatedUrl, { replaceUrl: true });
                }),
                withLatestFrom(this.#authenticationService.getUserData()),
                // do not log out if the user is not impersonating
                filter(([_, userData]) => !!userData?.impersonator?.username),
                exhaustMap(() => this.switchUser())
            ),
            this.#url$.pipe(
                filter(([action, _]) => action === IMPERSONATE_ACTION),
                exhaustMap(([_, url]) => this.switchUser(url.searchParams.get(ON_BEHALF_OF_QUERY_PARAM)))
            )
        ).subscribe();
    }

    #urlWithoutOnBehalfOf(userId: CipUserId): URL {
        const url = new URL(this.#window.location.href);
        if (url.searchParams.get(ON_BEHALF_OF_QUERY_PARAM)?.toLowerCase() === userId) {
            // to avoid relogin with the same user right after the logout
            url.searchParams.delete(ON_BEHALF_OF_QUERY_PARAM);
        }
        return url;
    }
}
